// /src/utils/ipGeolocation.js
// IP-based geolocation for guest users
// Uses multiple free services with caching to avoid rate limits

const CACHE_KEY = 'ournet_ip_location';
const CACHE_TTL_MS = 60 * 60 * 1000; // 1 hour cache

/**
 * Read the cached IP-location data synchronously, without triggering a fetch.
 * 2026-09-02 country-selection foundation build: single shared reader for the cache this
 * module writes — replaces three byte-identical inline localStorage-parsing IIFEs in
 * HeaderButtonsFeed.jsx (docs/reuse-backlog.md 2026-09-02 entry). Does not check CACHE_TTL_MS —
 * callers here only want the country/boundary signal for opening-view framing, where a slightly
 * stale IP location is harmless (unlike getLocationFromIP's own feed-content use, which re-fetches
 * on TTL expiry).
 * @returns {{latitude: number, longitude: number, city: string, region: string, country: string,
 *   countryCode: string, boundaryId?: number} | null}
 */
export function getCachedIpLocation() {
  try {
    const cached = localStorage.getItem(CACHE_KEY);
    if (!cached) return null;
    const { data } = JSON.parse(cached);
    return data || null;
  } catch (e) {
    return null;
  }
}

/**
 * Get approximate location from user's IP address
 * Uses caching and multiple fallback services
 * @returns {Promise<{latitude: number, longitude: number, city: string, region: string, country: string} | null>}
 */
export async function getLocationFromIP() {
  // Check cache first
  try {
    const cached = localStorage.getItem(CACHE_KEY);
    if (cached) {
      const { data, timestamp } = JSON.parse(cached);
      if (Date.now() - timestamp < CACHE_TTL_MS) {
        console.log('🌍 Using cached IP location:', data.city);
        return data;
      }
    }
  } catch (e) {
    // Cache read failed, continue to fetch
  }

  // Try multiple services in order of preference
  const services = [
    fetchFromIpApiCo,    // ipapi.co - 1000/month HTTPS
    fetchFromIpApi,      // ip-api.com - 45 req/min, HTTP only but reliable
  ];

  for (const fetchFn of services) {
    try {
      const result = await fetchFn();
      if (result) {
        // Cache successful result
        try {
          localStorage.setItem(CACHE_KEY, JSON.stringify({
            data: result,
            timestamp: Date.now()
          }));
        } catch (e) {
          // Cache write failed, continue anyway
        }
        return result;
      }
    } catch (error) {
      console.warn('IP service failed, trying next:', error.message);
    }
  }

  console.warn('All IP geolocation services failed');
  return null;
}

// ip-api.com - Free, 45 requests/minute, HTTP only (but works from localhost)
async function fetchFromIpApi() {
  const response = await fetch('http://ip-api.com/json/?fields=status,lat,lon,city,regionName,country');

  if (!response.ok) {
    throw new Error(`ip-api.com failed: ${response.status}`);
  }

  const data = await response.json();

  if (data.status !== 'success') {
    throw new Error('ip-api.com returned error status');
  }

  return {
    latitude: data.lat,
    longitude: data.lon,
    city: data.city || 'Unknown',
    region: data.regionName || '',
    country: data.country || '',
    countryCode: data.countryCode || ''
  };
}

// ipapi.co - Free HTTPS, 1000 requests/month
async function fetchFromIpApiCo() {
  const response = await fetch('https://ipapi.co/json/', {
    headers: { 'Accept': 'application/json' }
  });

  if (!response.ok) {
    throw new Error(`ipapi.co failed: ${response.status}`);
  }

  const data = await response.json();

  if (data.error) {
    throw new Error(`ipapi.co error: ${data.reason}`);
  }

  if (typeof data.latitude !== 'number' || typeof data.longitude !== 'number') {
    throw new Error('ipapi.co returned invalid coordinates');
  }

  return {
    latitude: data.latitude,
    longitude: data.longitude,
    city: data.city || 'Unknown',
    region: data.region || '',
    country: data.country_name || data.country || '',
    countryCode: data.country_code || ''
  };
}
